// Knowledge base helpers (Ordre 22-4): internal procedures, support documentation, FAQ, templates, policies. The rules live on the server.
import { hasPermission } from "./permissions";

const on = (user) => !!user?.features?.governance;
export const canReadKnowledge = (user) => on(user) && hasPermission(user, "knowledge:view");
export const canManageKnowledge = (user) => canReadKnowledge(user) && hasPermission(user, "knowledge:manage");

export const CATEGORIES = [["procedure", "Procédure interne"], ["support_doc", "Documentation support"], ["faq", "FAQ"], ["template", "Modèle"], ["policy", "Politique"]];
export const categoryLabel = (code) => (CATEGORIES.find(([id]) => id === code) || [])[1] || code;
export const STATUS_LABELS = { draft: "Brouillon", published: "Publié", archived: "Archivé" };
export const STATUS_TONES = { draft: "amber", published: "emerald", archived: "slate" };
export const VIEW_TABS = [["published", "Publiés"], ["to_review", "À relire"], ["drafts", "Brouillons"], ["archived", "Archivés"], ["all", "Tous"]];
export const REVIEW_PERIODS = [[3, "Tous les 3 mois"], [6, "Tous les 6 mois"], [12, "Tous les ans"], [24, "Tous les 2 ans"]];
export const ACTION_LABELS = {
  "new-draft": "Nouvelle version", "discard-draft": "Écarter le brouillon", publish: "Publier", review: "Marquer comme relu", archive: "Archiver", unarchive: "Réactiver",
};
export const ACTION_HINTS = {
  "new-draft": "Un brouillon est créé à partir de la version publiée. Les lecteurs continuent de voir la version publiée jusqu'à la publication.",
  "discard-draft": "Le brouillon est écarté ; la version publiée reste inchangée.",
  publish: "Le brouillon devient la version que tout le monde lit. La précédente reste dans l'historique, marquée remplacée.",
  review: "Vous confirmez que l'article est toujours juste. La prochaine relecture est reportée d'une période.",
  archive: "L'article n'est plus visible des lecteurs. Il reste dans l'historique et peut être réactivé.",
  unarchive: "L'article redevient lisible tel qu'il était avant son archivage.",
};
export const REASON_ACTIONS = ["archive", "unarchive"];
export const HISTORY_LABELS = {
  article_created: "Article créé", article_updated: "Informations modifiées", draft_saved: "Brouillon enregistré", article_new_draft: "Nouvelle version commencée",
  article_discard_draft: "Brouillon écarté", article_publish: "Version publiée", article_review: "Article relu", article_archive: "Article archivé", article_unarchive: "Article réactivé",
};

export const emptySection = () => ({ id: "", title: "", body: "" });

export function emptyArticleForm(article) {
  const source = article?.draft || article?.published;
  return {
    title: source?.title || "", category: article?.category || "", owner_id: article?.owner?.id || "", review_months: article?.review_months || 12,
    tags: (source?.tags || []).join(", "), sections: (source?.sections || []).map((s) => ({ id: s.id, title: s.title || "", body: s.body || "" })),
  };
}

export const parseTags = (text) => (text || "").split(",").map((t) => t.trim()).filter(Boolean);

export function articleProblem(form, { creating = false } = {}) {
  if ((form.title || "").trim().length < 3) return "Donnez un titre à l'article (3 caractères au moins).";
  if (creating && !form.category) return "Choisissez une catégorie.";
  if (creating && !form.owner_id) return "Nommez le propriétaire de l'article.";
  if (parseTags(form.tags).some((t) => !/^[a-z0-9][a-z0-9 -]*$/i.test(t) || t.length > 40)) return "Mots-clés : lettres, chiffres, espace et tiret seulement.";
  return "";
}

export const sectionsPayload = (sections) => sections.filter((s) => s.title.trim() || s.body.trim()).map((s) => ({ ...(s.id ? { id: s.id } : {}), title: s.title.trim() || null, body: s.body.trim() || null }));

export function draftPayload(form) {
  return { title: form.title.trim(), tags: parseTags(form.tags), sections: sectionsPayload(form.sections) };
}

export function createPayload(form) {
  return { ...draftPayload(form), category: form.category, owner_id: form.owner_id, review_months: form.review_months };
}

// A section body is PLAIN TEXT: paragraphs, « - » bullets, https links and [label](https://… or /internal/path). Never HTML.
const INLINE = /\[([^\]\n]{1,120})\]\((https:\/\/[^\s)]{3,300}|\/(?!\/)[^\s)]{0,300})\)|(https:\/\/[^\s<>"')]{3,300})/g;

export function inlineTokens(text) {
  const out = [];
  let last = 0;
  for (const match of String(text).matchAll(INLINE)) {
    if (match.index > last) out.push({ type: "text", text: text.slice(last, match.index) });
    const href = match[2] || match[3];
    out.push({ type: href.startsWith("/") ? "internal" : "external", text: match[1] || href, href });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
}

export function bodyBlocks(body) {
  const blocks = [];
  for (const line of String(body || "").split("\n")) {
    if (!line.trim()) { blocks.push({ type: "gap" }); continue; }
    const bullet = line.match(/^\s*[-•]\s+(.*)$/);
    if (bullet) {
      const last = blocks[blocks.length - 1];
      if (last?.type === "list") last.items.push(bullet[1]); else blocks.push({ type: "list", items: [bullet[1]] });
    } else {
      blocks.push({ type: "paragraph", text: line.trim() });
    }
  }
  return blocks.filter((b, i) => b.type !== "gap" || (i > 0 && i < blocks.length - 1 && blocks[i - 1].type !== "gap"));
}

export function knowledgeError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}
